import React, { useEffect, useState, useRef } from "react"
import { useParams, useNavigate } from "react-router"
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { httpOk, GET, POST, PUT, FILE } from 'helpers/api'
import { regexLogin, notify } from 'helpers/helpers'
import useFileDrop from 'pages/parts/useFileDrop'
import HintIcon from 'pages/parts/HintIcon'
import EmployeeAccruals from 'pages/main/employees/EmployeeAccruals'
import { hasPermission } from 'helpers/permissions'

function Index() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const fileInputRef = useRef(null);
	const reduxAccount = useSelector(state => state.account)

	const [poses, setPoses] = useState([])
	const [userRoles, setUserRoles] = useState([])
	const [courierTypes, setCourierTypes] = useState([])
	const [activeTab, setActiveTab] = useState(null)
	const [permissions, setPermissions] = useState([])
	const [selectedItem, setSelectedItem] = useState({})
	const [employeePositions, setEmployeePositions] = useState([])
	const [mainActiveTab, setMainActiveTab] = useState(1)
	const canViewAccruals = Boolean(id) && (hasPermission('employees_bonuses', reduxAccount) || hasPermission('employees_salaries', reduxAccount))
	/** permission ids from selected employee position — checked, cannot be unchecked */
	const [positionLockedPermissionIds, setPositionLockedPermissionIds] = useState([])

	async function createUpdate(e) {
		e.preventDefault()

		var selectedItemCopy = { ...selectedItem }
		selectedItemCopy.permissions = []
		selectedItemCopy.pos_id = reduxAccount.pos_id

		for (let i = 0; i < permissions.length; i++) {
			for (let j = 0; j < permissions[i]['permissions'].length; j++) {
				if (permissions[i]['permissions'][j]['checked']) {
					selectedItemCopy.permissions.push(permissions[i]['permissions'][j])
				}
			}
		}

		var response;
		if (selectedItemCopy.id) {
			response = await PUT('/employees', selectedItemCopy, { loader: true })
		} else {
			response = await POST('/employees', selectedItemCopy, { loader: true })
		}

		if (httpOk(response)) navigate('/employees')
	}

	function handleTaxRateChange(field, value) {
		setSelectedItem({
			...selectedItem,
			tax_rates: { ...(selectedItem.tax_rates || {}), [field]: value },
		})
	}

	function handleInputChange(e) {
		var selectedItemCopy = { ...selectedItem }
		switch (e.target.name) {
			case 'login':
				selectedItemCopy[e.target.name] = regexLogin(e.target.value)
				break;
			case 'car_model':
				selectedItemCopy.additional_info[e.target.name] = e.target.value
				break;
			case 'car_number':
				selectedItemCopy.additional_info[e.target.name] = e.target.value
				break;
			case 'pin_code':
				selectedItemCopy[e.target.name] = e.target.value.replace(/[^0-9]/g, '')
				break;
			case 'card_number':
				selectedItemCopy[e.target.name] = e.target.value.replace(/[^a-zA-Z0-9]/g, '')
				break;
			default:
				selectedItemCopy[e.target.name] = e.target.value
				break;
		}

		setSelectedItem(selectedItemCopy)
	}

	function hadleSelectRole(option) {
		setSelectedItem({ ...selectedItem, 'role': option ? option.role : null })

		if (option.role === 'ROLE_CASHIER') {
			var permissionsCopy = [...permissions]

			for (let i = 0; i < permissionsCopy[0]['permissions'].length; i++) {
				permissionsCopy[0]['permissions'][i]['checked'] = true
			}
			permissionsCopy[0]['checked'] = true

			setActiveTab(0)
			setPermissions(permissionsCopy)
		}
	}

	function mergePositionPermissionsIntoTree(prevPermissions, positionPermissions = []) {
		const lockedIds = (positionPermissions || []).map(p => p.id)
		return applyPositionPermissionDiff(prevPermissions, [], lockedIds)
	}

	/** Смена должности: снять старые position-галочки, поставить новые; персональные сверх должности оставить */
	function applyPositionPermissionDiff(prevPermissions, oldLockedIds = [], newLockedIds = []) {
		const oldSet = new Set(oldLockedIds || [])
		const newSet = new Set(newLockedIds || [])
		return prevPermissions.map(group => {
			const groupPermissions = (group.permissions || []).map(permission => {
				let checked = Boolean(permission.checked)
				if (oldSet.has(permission.id) && !newSet.has(permission.id)) {
					checked = false
				}
				if (newSet.has(permission.id)) {
					checked = true
				}
				return {
					...permission,
					checked,
				}
			})
			return {
				...group,
				permissions: groupPermissions,
				checked: groupPermissions.length > 0 && groupPermissions.every(p => p.checked),
			}
		})
	}

	function isPermissionLocked(permissionId) {
		return positionLockedPermissionIds.includes(permissionId)
	}

	async function handleSelectEmployeePosition(option) {
		const employeePositionId = option ? option.id : null
		const previousLockedIds = [...positionLockedPermissionIds]
		setSelectedItem({ ...selectedItem, employee_position_id: employeePositionId })

		if (!employeePositionId) {
			// Должность снята: галочки должности становятся персональными (можно снять вручную)
			setPositionLockedPermissionIds([])
			return
		}

		const response = await GET(`/employee-positions/${employeePositionId}`)
		if (httpOk(response)) {
			const lockedIds = (response.data?.permissions || []).map(p => p.id)
			setPositionLockedPermissionIds(lockedIds)
			setPermissions(prev => applyPositionPermissionDiff(prev, previousLockedIds, lockedIds))
		}
	}

	async function uploadImageFile(file) {
		if (!file) return

		if (!file.type?.startsWith('image/')) {
			notify(t('image2'))
			return
		}

		let formData = new FormData();
		formData.append("image", file);
		if (selectedItem.id) formData.append("id", selectedItem.id);

		var response = await FILE(`/employees-image`, formData, {}, { loader: true });

		if (httpOk(response)) setSelectedItem({ ...selectedItem, 'image': response.data.url })

		if (fileInputRef.current) fileInputRef.current.value = '';
	}

	async function fileUpload(e) {
		await uploadImageFile(e.target.files[0])
	}

	const { dropProps, dropStyle } = useFileDrop(uploadImageFile)

	function selectAll(checked) {
		var permissionsCopy = [...permissions]

		for (let i = 0; i < permissionsCopy[activeTab]['permissions'].length; i++) {
			const row = permissionsCopy[activeTab]['permissions'][i]
			if (isPermissionLocked(row.id)) {
				permissionsCopy[activeTab]['permissions'][i]['checked'] = true
			} else {
				permissionsCopy[activeTab]['permissions'][i]['checked'] = checked
			}
		}
		permissionsCopy[activeTab]['checked'] = permissionsCopy[activeTab]['permissions'].every(p => p.checked)

		setPermissions(permissionsCopy)
	}

	function selectOne(checked, permissionId) {
		var permissionsCopy = [...permissions]
		const permission = permissionsCopy[activeTab]?.permissions?.find(item => item.id === permissionId)
		if (!permission || isPermissionLocked(permission.id)) {
			return
		}

		permission.checked = checked
		permissionsCopy[activeTab]['checked'] = permissionsCopy[activeTab]['permissions'].every(p => p.checked)

		setPermissions(permissionsCopy)
	}

	function renderPermissionRow(item) {
		return (
			<div
				className={`d-flex justify-content-between ${isPermissionLocked(item.id) ? '' : 'bg-hover-grey'}`}
				key={item.id}
				onClick={() => {
					if (isPermissionLocked(item.id)) return
					selectOne(!item.checked, item.id)
				}}>
				<div style={returnStyle(item)} title={item.value}>
					{item.order_number}. {item.name}
				</div>
				<div className="form-check" style={{ justifyContent: 'flex-end', width: '10%' }}>
					<input
						className="form-check-input"
						type="checkbox"
						checked={Boolean(item.checked)}
						disabled={isPermissionLocked(item.id)}
						onChange={(e) => {
							e.stopPropagation()
							selectOne(e.target.checked, item.id)
						}}
					/>
				</div>
			</div>
		)
	}

	function renderActiveCategoryPermissions() {
		const activeCategory = permissions[activeTab]
		if (!activeCategory) {
			return null
		}

		if (activeCategory.sections?.length > 0) {
			return activeCategory.sections.map((section, sectionIndex) => {
				const sectionPermissionIds = new Set((section.permissions || []).map(item => item.id))
				const sectionRows = (activeCategory.permissions || []).filter(item => sectionPermissionIds.has(item.id))

				return (
					<div key={section.id || section.code || sectionIndex} className={sectionIndex > 0 ? 'mt-3' : ''}>
						<div
							className="fw-semibold text-muted pb-1 mb-2"
							style={{ borderBottom: '1px solid var(--dark-border, #ddd)' }}>
							{section.name}
						</div>
						{sectionRows.map(item => renderPermissionRow(item))}
					</div>
				)
			})
		}

		return activeCategory.permissions?.map(item => renderPermissionRow(item))
	}

	function returnStyle(item) {
		var style = {}
		style.width = '90%'
		style.borderBottom = '1px solid var(--dark-border, #eee)'
		if (item.parent_id) {
			style.marginLeft = '20px'
			return style
		}
		if (!item.parent_id) {
			style.fontWeight = '600'
			return style
		}
	}

	async function getPoses() {
		const response = await GET('/helper/poses-head')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getUserRoles() {
		const response = await GET('/helper/user-roles')
		if (httpOk(response)) setUserRoles(response.data)
	}

	async function getEmployeePositions() {
		const response = await GET(`/helper/employee-positions`)
		if (httpOk(response)) setEmployeePositions(response.data)
	}

	async function getCourierTypes() {
		const response = await GET("/helper/courier-types")
		if (httpOk(response)) setCourierTypes(response.data)
	}

	async function getPermissions(user_login = null) {
		var response = await GET(`/helper/permissions?user_login=${user_login}`)
		if (httpOk(response)) {
			setActiveTab(0)
			setPermissions(response.data)
		}
	}

	async function getData() {
		if (id) {
			var response = await GET(`/employees/${id}`, {}, { loader: true })

			if (httpOk(response)) {
				setSelectedItem(response.data)
				const permissionsResponse = await GET(`/helper/permissions?user_login=${response.data.login}`)
				if (httpOk(permissionsResponse)) {
					setActiveTab(0)
					let nextPermissions = permissionsResponse.data
					if (response.data.employee_position_id) {
						const positionResponse = await GET(`/employee-positions/${response.data.employee_position_id}`)
						if (httpOk(positionResponse)) {
							const lockedIds = (positionResponse.data?.permissions || []).map(p => p.id)
							setPositionLockedPermissionIds(lockedIds)
							nextPermissions = mergePositionPermissionsIntoTree(permissionsResponse.data, positionResponse.data?.permissions || [])
						} else {
							setPositionLockedPermissionIds([])
						}
					} else {
						setPositionLockedPermissionIds([])
					}
					setPermissions(nextPermissions)
				}
			}
		} else {
			setPositionLockedPermissionIds([])
			getPermissions()
		}
	}

	useEffect(() => {
		getData()
		getPoses()
		getUserRoles()
		getCourierTypes()
		getEmployeePositions()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('staff')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						{!id ?
							<h6>{t('create')}</h6>
							:
							<h6>{t('edit')}</h6>
						}
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex gap-2">
						{/* Три вкладки в 30% не помещаются на 1280px — при вкладке «Начисления» колонка не уже 380px */}
						<div style={{ 'width': '30%', 'minWidth': canViewAccruals ? '380px' : undefined }}>
							<div className="tabs tabs-sm mb-2">
								<ul>
									<li className={mainActiveTab === 1 ? 'active' : ''}
										onClick={() => setMainActiveTab(1)}>
										<span className="link">{t('main')}</span>
									</li>
									<li className={mainActiveTab === 2 ? 'active ' : ''}
										onClick={() => setMainActiveTab(2)}>
										<span className="link">{t('additionally')}</span>
									</li>
									{/* Что назначено: оплата по времени, мотивации, налоги — ставки видят только с правом на них */}
									{canViewAccruals &&
										<li className={mainActiveTab === 3 ? 'active ' : ''}
											onClick={() => setMainActiveTab(3)}>
											<span className="link">{t('accruals')}</span>
										</li>
									}
								</ul>
							</div>
							<form autoComplete="off">
								<div className={`${mainActiveTab === 1 ? '' : 'd-none'}`}>
									<div className="form-group">
										<label>{t('system_name')}<span className="required-mark">*</span></label>
										<input type="text" className="form-control" name="first_name"
											value={selectedItem.first_name ?? ''}
											onChange={(e) => handleInputChange(e)} />
									</div>
									<div className="d-flex flex-column w-100">
										<div className="form-group">
											<label>{t('preview')}</label>
											<Select
												isMulti
												options={poses}
												value={selectedItem.poses}
												onChange={(options) => setSelectedItem({ ...selectedItem, 'poses': options })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('roles')}<span className="required-mark">*</span></label>
												<Select
													options={userRoles}
													value={userRoles.find(option => option.role === selectedItem.role)}
													onChange={(option) => hadleSelectRole(option)}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.role}
												/>
											</div>
											<div className="form-group w-100">
												<label>{t('position')}</label>
												<Select
													isClearable
													options={employeePositions}
													value={employeePositions.find(option => option.id === selectedItem.employee_position_id)}
													onChange={handleSelectEmployeePosition}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="form-group">
											<label>{t('phone')}</label>
											<input type="number" className="form-control" name="phone"
												placeholder="xxxxxxxxxxxx"
												value={selectedItem.phone ?? ''}
												onChange={(e) => handleInputChange(e)} />
										</div>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('login')}</label>
												<input type="text" className="form-control" name="login"
													disabled={id}
													value={selectedItem.login ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
											<div className="form-group w-100">
												<label>{t('password')}</label>
												<input type="text" className="form-control" name="password"
													value={selectedItem.password ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
										</div>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('pin_code')}</label>
												<input type="number" className="form-control" name="pin_code"
													value={selectedItem.pin_code ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
											<div className="form-group w-100">
												<label>{t('card_number')}</label>
												<input type="text" className="form-control" name="card_number"
													value={selectedItem?.card_number ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
											<div className="form-group w-100">
												<label>{t('code')}</label>
												<input type="number" className="form-control" name="employee_code"
													value={selectedItem?.employee_code ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
										</div>
										<div className="form-group">
											<label>{t('note')}</label>
											<input type="text" className="form-control" name="note"
												value={selectedItem.note ?? ''}
												onChange={(e) => handleInputChange(e)} />
										</div>
									</div>
								</div>
								<div className={`${mainActiveTab === 2 ? '' : 'd-none'}`}>
									<div>
										<div className="con-upload" {...dropProps}>
											<div className="con-img-upload">
												{selectedItem.image &&
													<div className="img-upload" style={dropStyle}>
														<button type="button" className="btn-x-file"
															onClick={() => setSelectedItem({ ...selectedItem, 'image': '' })}>
															<i className="uil-times"></i>
														</button>
														<img src={`https://backend.mison.uz/uploads/users/${selectedItem.image}`}
															style={{ maxWidth: 'none', maxHeight: '100%' }} alt={selectedItem.image} />
													</div>
												}
												{!selectedItem.image &&
													<div className="con-input-upload" style={dropStyle}>
														<input type="file"
															ref={fileInputRef}
															onChange={(e) => fileUpload(e)} />
														<span className="text-input fz14">
															{t('image2')}
														</span>
													</div>
												}
											</div>
										</div>

										<div className="form-group w-100">
											<label>{t('courier_types')}</label>
											<Select
												isClearable
												options={courierTypes}
												value={courierTypes.find(option => option.id === selectedItem.courier_type_id)}
												onChange={(option) => setSelectedItem({ ...selectedItem, 'courier_type_id': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>

										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('car_model')}</label>
												<input type="text" className="form-control" name="car_model"
													value={selectedItem?.additional_info?.car_model ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
											<div className="form-group w-100">
												<label>{t('car_number')}</label>
												<input type="text" className="form-control" name="car_number"
													value={selectedItem?.additional_info?.car_number ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
										</div>

										<hr />
										<h3>{t('passport')}</h3>
										<div className="form-group">
											<label>{t('tin')}</label>
											<input type="text" className="form-control" name="tin"
												value={selectedItem.tin ?? ''}
												onChange={(e) => handleInputChange(e)} />
										</div>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('serial')}</label>
												<input type="text" className="form-control" name="series"
													value={selectedItem.series ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
											<div className="form-group w-100">
												<label>{t('number')}</label>
												<input type="text" className="form-control" name="number"
													value={selectedItem.number ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
										</div>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('issued_at')}</label>
												<input type="date" className="form-control" name="issued_at"
													value={selectedItem.issued_at ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
											<div className="form-group w-100">
												<label>{t('valid_until')}</label>
												<input type="date" className="form-control" name="valid_until"
													value={selectedItem.valid_until ?? ''}
													onChange={(e) => handleInputChange(e)} />
											</div>
										</div>

										<div className="form-group w-100">
											<label>{t("issued_by")}</label>
											<textarea className="form-control" name="issued_by"
												value={selectedItem.issued_by ?? ''}
												onChange={(e) => handleInputChange(e)} />
										</div>

										{/* Личные ставки налогов поверх должности: пусто — ставка должности */}
										{selectedItem.id && selectedItem.position_tax_rates &&
											<>
												<hr />
												<h3>{t('salary_taxes')}<HintIcon text={`${t('salary_taxes_hint')} ${t('salary_taxes_own_hint')}.`} /></h3>
												<div className="d-flex gap-2">
													{['income_tax_rate', 'pension_rate'].map((field) => (
														<div className="form-group w-100" key={field}>
															<label>{t(field)}</label>
															<input type="number" className="form-control" min="0" max="100" step="0.01"
																placeholder={String(Number(selectedItem.position_tax_rates[field] ?? 0))}
																value={selectedItem.tax_rates?.[field] ?? ''}
																onChange={(e) => handleTaxRateChange(field, e.target.value)} />
														</div>
													))}
												</div>
												<div className="d-flex gap-2">
													<div className="form-group w-100">
														<label>{t('social_tax_rate')}</label>
														<input type="number" className="form-control" min="0" max="100" step="0.01"
															placeholder={String(Number(selectedItem.position_tax_rates.social_tax_rate ?? 0))}
															value={selectedItem.tax_rates?.social_tax_rate ?? ''}
															onChange={(e) => handleTaxRateChange('social_tax_rate', e.target.value)} />
													</div>
													<div className="w-100"></div>
												</div>
											</>
										}

									</div>
								</div>
								{canViewAccruals && mainActiveTab === 3 &&
									<EmployeeAccruals
										employeeId={selectedItem.id}
										taxRates={selectedItem.tax_rates}
										positionTaxRates={selectedItem.position_tax_rates} />
								}
							</form>
						</div>
						<div style={{ 'width': '70%' }}>
							<div className="d-flex gap-2">
								<div className="d-flex flex-column" style={{ 'width': '30%' }}>
									{permissions?.map((item, index) => (
										<div key={index} className="cursor"
											style={activeTab === index ? { 'borderBottom': '1px solid #000' } : {}}
											onClick={() => setActiveTab(index)}>
											<span className="link">{item.name}</span>
										</div>
									))}
								</div>
								<div style={{ 'width': '70%' }}>
									{activeTab >= 0 &&
										<div style={{ 'height': '600px', 'overflow': 'auto' }}>
											{permissions.map((item, index) => (
												index === activeTab &&
												<div className="d-flex justify-content-between" key={index}>
													<h4>{t('all')}</h4>
													<div className="form-check" key={index}>
														<input className="form-check-input" type="checkbox" id="flexCheckChecked"
															checked={item.checked ? true : false}
															disabled={Boolean(permissions[activeTab]?.permissions?.length) &&
																permissions[activeTab].permissions.every(p => isPermissionLocked(p.id))}
															onChange={(e) => selectAll(e.target.checked)} />
													</div>
												</div>
											))}

											{renderActiveCategoryPermissions()}
										</div>
									}
								</div>
							</div>
						</div>
					</div>

					<div className="d-flex justify-content-end gap-2 mt-5">
						<button type="button" className="btn btn-warning btn-rounded btn-wide"
							onClick={() => navigate(-1)}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary btn-rounded btn-wide"
							onClick={createUpdate}>
							{t('save')}
						</button>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index